/**
 * AdminView — top-level admin surface, gated to role=admin.
 *
 * Sub-tabs:
 *   - Users    : invite, edit role, disable accounts (real today)
 *   - Company Brain : create/edit the org's Brand Brain (real today)
 *   - Settings : integration + runtime status surface (real today)
 *   - Audit    : agent activity & approvals ledger (real today)
 *
 * Auth gate is enforced upstream in App.tsx (non-admins are bounced
 * before this component mounts), and again here as a belt-and-suspenders
 * "access denied" panel. Defense in depth costs us nothing.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  BrainCircuit,
  Check,
  CircleDashed,
  Compass,
  Database,
  History,
  KeyRound,
  Mail,
  Plug,
  Plus,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserCircle2,
  UserX,
  Users as UsersIcon,
  X,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { CompanyBrainEditor } from '../components/CompanyBrainEditor';
import { SetupTab } from '../components/SetupTab';
import { IntegrationsTab } from '../components/IntegrationsTab';
import type { ViewKey } from '../components/TopMenu';
import type { ProjectsTab } from './ProjectsView';
import { DecisionLedger } from '../components/DecisionLedger';
import { AgentDecisionLedger } from '../components/AgentDecisionLedger';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { useAuth, ALL_ROLES } from '../lib/auth/AuthProvider';
import { C } from '../lib/tokens';
import {
  bootstrapAppUsersIfEmpty,
  deleteAppUser,
  inviteAppUser,
  setAppUserRole,
  setAppUserStatus,
  subscribeAppUsers,
} from '../lib/db/appUsers';
import type { AppUser, AppUserRole, AppUserStatus, ApprovalItem } from '../lib/db/types';
import { sendTransactionalEmail } from '../lib/email';
import { resolveApproval, subscribeApprovals } from '../lib/agent/approvals';
import { subscribeActivity } from '../lib/db/activity';
import { toLedgerEntries } from '../lib/ledger/verifyChain';
import {
  chainAlertFromStatus,
  registerChainMonitor,
  runChainCheck,
  type ChainMonitorScheduler,
  type ChainStatus,
} from '../lib/ledger/chainMonitor';
import { summarizeAction } from '../lib/agent/actions';
import * as argoWorker from '../lib/agent/argoWorker';
import * as enrich from '../lib/agent/enrich';
import * as meetNotes from '../lib/agent/meetNotes';
import * as slackNotify from '../lib/agent/slackNotify';
import { modelsByProvider, findModel, PROVIDER_LABEL } from '../lib/llm/models';
import { getSelectedModel, setSelectedModel } from '../lib/llm/modelPreference';

export type AdminTab = 'users' | 'setup' | 'integrations' | 'brain' | 'settings' | 'audit';

interface AdminViewProps {
  initialTab?: AdminTab;
  /** App-level navigation — lets Setup's governance section deep-link to
   *  Dev → Agents (autonomy tiers). Optional: panels degrade to plain
   *  directions when the host can't navigate. */
  onNavigate?: (view: ViewKey) => void;
  onSelectProjectsTab?: (tab: ProjectsTab) => void;
}

interface TabMeta {
  id: AdminTab;
  label: string;
  icon: LucideIcon;
}

const tabs: TabMeta[] = [
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'setup', label: 'Setup', icon: Compass },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'brain', label: 'Company Brain', icon: BrainCircuit },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'audit', label: 'Audit log', icon: History },
];

export function AdminView({
  initialTab = 'users',
  onNavigate,
  onSelectProjectsTab,
}: AdminViewProps) {
  const { hasRole } = useAuth();
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const lastInitial = useRef<AdminTab>(initialTab);

  useEffect(() => {
    if (initialTab !== lastInitial.current) {
      setTab(initialTab);
      lastInitial.current = initialTab;
    }
  }, [initialTab]);

  if (!hasRole('admin')) {
    return <AccessDenied />;
  }

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <span className="t-eyebrow">Admin</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            Organization
          </h1>
          <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
            Manage who can sign in, what they can do, and what's been done.
          </p>
        </div>
      </header>

      <GlassPanel variant="subtle" animate={false} style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className="glass-btn"
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${active ? 'rgba(255,0,255,0.45)' : 'rgba(255,255,255,0.10)'}`,
                  background: active
                    ? 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))'
                    : 'transparent',
                  color: active ? '#fff' : C.textMuted,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Icon size={13} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </GlassPanel>

      {tab === 'users' && <UsersTab />}
      {tab === 'setup' && (
        <SetupTab
          onOpenBrain={() => setTab('brain')}
          onOpenAudit={() => setTab('audit')}
          onOpenAgents={
            onNavigate
              ? () => {
                  onSelectProjectsTab?.('agents');
                  onNavigate('projects');
                }
              : undefined
          }
        />
      )}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'brain' && <CompanyBrainEditor />}
      {tab === 'settings' && <SettingsTab onOpenSetup={() => setTab('setup')} />}
      {tab === 'audit' && (
        <div style={{ display: 'grid', gap: 24 }}>
          <ChainIntegrityBanner />
          <section style={{ display: 'grid', gap: 10 }}>
            <div>
              <h2 className="t-section-title" style={{ margin: 0 }}>Decision Ledger</h2>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                Every governed change — proposed, approved, executed, undone — append-only,
                provenance-stamped, and tamper-evident. Verify the chain or export the audit pack.
              </p>
            </div>
            <DecisionLedger />
          </section>
          <section style={{ display: 'grid', gap: 10 }}>
            <div>
              <h2 className="t-section-title" style={{ margin: 0 }}>Sealed decision chain</h2>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                The first-class, SHA-256 hash-chained ledger: every approved agent action and every
                undo, append-only and tamper-evident. Verify the chain in one pass.
              </p>
            </div>
            <AgentDecisionLedger />
          </section>
          <section style={{ display: 'grid', gap: 10 }}>
            <div>
              <h2 className="t-section-title" style={{ margin: 0 }}>Approvals</h2>
              <p className="t-meta" style={{ margin: '3px 0 0' }}>
                Every action an agent proposed and what happened to it.
              </p>
            </div>
            <AuditTab canMutate={hasRole('staff')} />
          </section>
        </div>
      )}
    </div>
  );
}

// --- Ledger chain integrity banner ------------------------------------------

/** How often the on-view monitor re-verifies the sealed chain. */
const CHAIN_VERIFY_INTERVAL_MS = 5 * 60_000;

/** setInterval-backed scheduler for the pure chain monitor's tick loop. */
const intervalScheduler: ChainMonitorScheduler = {
  schedule(tick, intervalMs) {
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  },
};

/**
 * Continuous, human-independent proof of the governance moat: verifies the
 * Decision Ledger chain on load, whenever the ledger changes, and on a
 * scheduled interval — surfacing a PERSISTENT red banner (plus a toast
 * carrying the notifications-shaped draft) if the chain ever breaks, and a
 * "last verified" stamp while it holds. All decisions are the pure helpers
 * in src/lib/ledger/chainMonitor.ts; this is read → verify → render only.
 */
function ChainIntegrityBanner() {
  const [status, setStatus] = useState<ChainStatus | null>(null);
  const rowsRef = useRef<Parameters<typeof toLedgerEntries>[0]>([]);
  const alertedRef = useRef(false);
  const { push } = useToast();

  useEffect(() => {
    // On-load + on-change verification: every ledger snapshot is re-walked.
    const unsubRows = subscribeActivity((rows) => {
      rowsRef.current = rows;
      setStatus(runChainCheck(toLedgerEntries([...rows]), Date.now()));
    });
    // Scheduled re-verification, driven by shouldReverify inside the monitor.
    const cancel = registerChainMonitor(intervalScheduler, {
      loadEntries: () => toLedgerEntries([...rowsRef.current]),
      onStatus: setStatus,
      intervalMs: CHAIN_VERIFY_INTERVAL_MS,
    });
    return () => {
      unsubRows();
      cancel();
    };
  }, []);

  // Emit the notification draft (once per break) through the toast surface.
  useEffect(() => {
    if (!status || status.ok) {
      alertedRef.current = false;
      return;
    }
    if (alertedRef.current) return;
    alertedRef.current = true;
    const alert = chainAlertFromStatus(status);
    if (alert.notification) {
      push({ kind: 'error', text: alert.notification.title, detail: alert.notification.detail });
    }
  }, [status, push]);

  if (!status) return null;
  const alert = chainAlertFromStatus(status);
  const verifiedAt = new Date(status.lastVerifiedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  if (alert.severity === 'critical') {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderRadius: 12,
          border: '1px solid rgba(255,80,80,0.55)',
          background: 'rgba(255,60,60,0.14)',
          color: C.text,
          fontSize: 13,
        }}
      >
        <ShieldAlert size={17} strokeWidth={2} style={{ color: '#ff6b6b', flexShrink: 0 }} />
        <span style={{ fontWeight: 600 }}>{alert.message}</span>
        <span className="t-meta" style={{ marginLeft: 'auto', flexShrink: 0 }}>
          Last verified {verifiedAt}
        </span>
      </div>
    );
  }

  return (
    <div
      role="status"
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}
    >
      <ShieldCheck size={14} strokeWidth={2} style={{ color: C.green, flexShrink: 0 }} />
      <span className="t-meta">
        Ledger chain intact · {status.entryCount} sealed{' '}
        {status.entryCount === 1 ? 'entry' : 'entries'} · last verified {verifiedAt} · auto-recheck
        every {Math.round(CHAIN_VERIFY_INTERVAL_MS / 60_000)}m
      </span>
    </div>
  );
}

// --- Access denied panel ---------------------------------------------------

function AccessDenied() {
  return (
    <div className="app-container">
      <GlassPanel variant="hero" style={{ padding: '40px 32px', maxWidth: 620, margin: '40px auto 0' }}>
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            gap: 12,
            textAlign: 'center',
          }}
        >
          <span
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,0,255,0.28)',
              color: C.magentaSoft,
            }}
          >
            <ShieldAlert size={26} strokeWidth={1.75} />
          </span>
          <h3 className="t-section-title" style={{ margin: 0 }}>Admin only</h3>
          <p className="t-body" style={{ color: C.textMuted, margin: 0, maxWidth: 420 }}>
            This area requires admin permissions. If you think you should
            have access, ask another admin to bump your role under{' '}
            <strong>Admin → Users</strong>.
          </p>
        </div>
      </GlassPanel>
    </div>
  );
}

// --- Users tab -------------------------------------------------------------

function UsersTab() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<AppUserRole | null>(null);
  const [statusFilter, setStatusFilter] = useState<AppUserStatus | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AppUser | null>(null);
  const { user: me } = useAuth();
  const { push } = useToast();

  useEffect(() => {
    let active = true;
    bootstrapAppUsersIfEmpty().catch(() => undefined);
    const unsub = subscribeAppUsers((records) => {
      if (active) setUsers(records);
    });
    return () => {
      active = false;
      unsub();
    };
  }, []);

  const counts = useMemo(() => {
    const acc = { admin: 0, staff: 0, viewer: 0, pending: 0, disabled: 0 };
    for (const u of users) {
      acc[u.role] = (acc[u.role] ?? 0) + 1;
      if (u.status === 'pending') acc.pending++;
      if (u.status === 'disabled') acc.disabled++;
    }
    return acc;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => !roleFilter || u.role === roleFilter)
      .filter((u) => !statusFilter || u.status === statusFilter)
      .filter((u) => {
        if (!q) return true;
        const hay = (u.displayName + ' ' + u.email + ' ' + (u.notes ?? '')).toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [users, search, roleFilter, statusFilter]);

  const onChangeRole = async (u: AppUser, role: AppUserRole) => {
    if (role === u.role) return;
    if (me?.uid === u.id && role !== 'admin') {
      push({
        kind: 'warning',
        text: 'You cannot demote yourself.',
        detail: 'Have another admin change your role.',
      });
      return;
    }
    try {
      await setAppUserRole(u.id, role);
      push({
        kind: 'success',
        text: `${u.displayName} is now ${role}`,
        detail: 'Once Firebase is live, the user must re-auth to refresh claims.',
      });
    } catch (err) {
      push({ kind: 'error', text: 'Could not change role', detail: String(err) });
    }
  };

  const onToggleStatus = async (u: AppUser) => {
    if (me?.uid === u.id) {
      push({ kind: 'warning', text: 'You cannot disable yourself.' });
      return;
    }
    const next: AppUserStatus = u.status === 'active' ? 'disabled' : 'active';
    try {
      await setAppUserStatus(u.id, next);
      push({
        kind: 'success',
        text: `${u.displayName} ${next === 'active' ? 'enabled' : 'disabled'}`,
      });
    } catch (err) {
      push({ kind: 'error', text: 'Could not update status', detail: String(err) });
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteAppUser(confirmDelete.id);
      push({ kind: 'success', text: `Removed ${confirmDelete.displayName}` });
    } catch (err) {
      push({ kind: 'error', text: 'Could not remove user', detail: String(err) });
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Toolbar */}
      <div className="kanban-toolbar">
        <div className="kanban-search">
          <Search size={13} className="kanban-search__icon" />
          <input
            className="glass-input"
            placeholder="Search users by name, email, or notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>

        <div className="kanban-phasefilter" role="tablist">
          {ALL_ROLES.map((r) => {
            const active = roleFilter === r;
            const accent =
              r === 'admin' ? C.magenta : r === 'staff' ? C.blueElectric : C.orange;
            return (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={active}
                className="kanban-phasefilter__btn"
                data-active={active}
                style={
                  {
                    ['--phase-accent' as any]: accent,
                    ['--phase-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
                onClick={() => setRoleFilter(active ? null : r)}
              >
                <span className="kanban-phasefilter__dot" style={{ background: accent }} />
                <span style={{ textTransform: 'capitalize' }}>{r}</span>
                <span className="kanban-phasefilter__count">{counts[r]}</span>
              </button>
            );
          })}
          {(['pending', 'disabled'] as AppUserStatus[]).map((s) => {
            const active = statusFilter === s;
            const accent = s === 'pending' ? C.orange : C.orangeWarm;
            const count = s === 'pending' ? counts.pending : counts.disabled;
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={active}
                className="kanban-phasefilter__btn"
                data-active={active}
                style={
                  {
                    ['--phase-accent' as any]: accent,
                    ['--phase-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
                onClick={() => setStatusFilter(active ? null : s)}
              >
                <span className="kanban-phasefilter__dot" style={{ background: accent }} />
                <span style={{ textTransform: 'capitalize' }}>{s}</span>
                <span className="kanban-phasefilter__count">{count}</span>
              </button>
            );
          })}
          {(roleFilter || statusFilter) && (
            <button
              type="button"
              className="glass-btn"
              onClick={() => {
                setRoleFilter(null);
                setStatusFilter(null);
              }}
              style={{ padding: '6px 10px' }}
            >
              Clear
            </button>
          )}
        </div>

        <div style={{ flex: 1 }} />
        <button
          className="glass-btn glass-btn--primary"
          onClick={() => setInviteOpen(true)}
        >
          <Plus size={13} /> Invite user
        </button>
      </div>

      {/* Table */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: 0, overflow: 'hidden' }}>
        <div className="users-table">
          <div className="users-table__head">
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Status</span>
            <span>Last sign-in</span>
            <span>Actions</span>
          </div>
          {filtered.length === 0 ? (
            <div className="crm-empty">
              <UsersIcon size={20} strokeWidth={1.75} />
              <span>
                {users.length === 0
                  ? 'No users yet — invite your first.'
                  : 'No users match your filters.'}
              </span>
            </div>
          ) : (
            filtered.map((u) => {
              const isMe = me?.uid === u.id;
              return (
                <div
                  key={u.id}
                  className={`users-row${u.status === 'disabled' ? ' is-disabled' : ''}`}
                >
                  <span className="users-row__name">
                    <span
                      className="users-row__avatar"
                      data-role={u.role}
                      aria-hidden="true"
                    >
                      {u.displayName.slice(0, 1).toUpperCase()}
                    </span>
                    <span>
                      {u.displayName}
                      {isMe && <span className="users-row__you">  · you</span>}
                    </span>
                  </span>
                  <span className="users-row__email">{u.email}</span>
                  <span>
                    <select
                      className="glass-input users-row__role"
                      value={u.role}
                      onChange={(e) =>
                        onChangeRole(u, e.target.value as AppUserRole)
                      }
                      aria-label={`Change role for ${u.displayName}`}
                    >
                      {ALL_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </span>
                  <span>
                    <StatusPill status={u.status} />
                  </span>
                  <span className="t-meta">
                    {u.lastSignInAt ? fmtRelative(u.lastSignInAt) : '—'}
                  </span>
                  <span className="users-row__actions">
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={() => onToggleStatus(u)}
                      disabled={isMe}
                      title={u.status === 'active' ? 'Disable' : 'Enable'}
                    >
                      <UserX size={12} />
                      {u.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="glass-btn"
                      onClick={() => setConfirmDelete(u)}
                      disabled={isMe}
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </GlassPanel>

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onInvited={(u) => {
            push({ kind: 'success', text: `Invited ${u.email}` });
            setInviteOpen(false);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Remove ${confirmDelete.displayName}?`}
          body={`This permanently removes ${confirmDelete.email} from the user list. Their existing audit entries stay.`}
          confirmLabel="Remove user"
          danger
          onConfirm={onDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// --- Invite dialog ---------------------------------------------------------

function InviteDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: (user: AppUser) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AppUserRole>('staff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/.+@.+\..+/.test(email)) {
      setError('Use a full email address.');
      return;
    }
    setBusy(true);
    try {
      const created = await inviteAppUser({
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        role,
      });
      
      await sendTransactionalEmail({
        to: email.trim(),
        subject: "You're invited to Argo OS",
        textContent: `You have been invited to join Argo OS as a ${role}.`,
        htmlContent: `<p>You have been invited to join Argo OS as a <strong>${role}</strong>.</p>`
      });

      onInvited(created);
    } catch (err) {
      // Show the error inline in the dialog only — no double-fire to
      // the toast shelf. The toast is for parent-driven success.
      const msg = err instanceof Error ? err.message : 'Could not invite user.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="account-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      data-menu-portal="true"
    >
      <div className="account-modal-wrap" style={{ width: 'min(540px, 100%)' }}>
        <GlassPanel variant="hero" className="account-modal" animate>
          <header
            className="account-modal__head"
            style={{ alignItems: 'center', borderBottom: 'none', paddingBottom: 4 }}
          >
            <Mail size={18} style={{ color: C.magentaSoft, marginRight: 6 }} />
            <div style={{ flex: 1 }}>
              <span className="t-eyebrow">Admin</span>
              <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
                Invite user
              </h2>
            </div>
          </header>
          <form onSubmit={submit} style={{ display: 'grid', gap: 14, padding: '12px 0 0' }}>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>Email *</div>
              <input
                className="glass-input"
                type="email"
                placeholder="name@argoiq.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                autoFocus
                required
              />
            </div>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>Display name (optional)</div>
              <input
                className="glass-input"
                placeholder="Auto-generated from email if empty"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <div className="t-eyebrow" style={{ marginBottom: 4 }}>Role</div>
              <select
                className="glass-input"
                value={role}
                onChange={(e) => setRole(e.target.value as AppUserRole)}
                disabled={busy}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {ALL_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {error && (
              <div className="login-banner" role="alert">
                {error}
              </div>
            )}
            <p className="t-meta" style={{ margin: 0 }}>
              Once Firebase is wired, this sends a sign-in link to the
              email and the user finishes onboarding from there. Today
              the row appears as <em>pending</em> until they sign in.
            </p>
            <footer
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                paddingTop: 6,
                borderTop: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <button
                type="button"
                className="glass-btn"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="glass-btn glass-btn--primary"
                disabled={busy}
              >
                <Plus size={13} />
                {busy ? 'Sending invite…' : 'Send invite'}
              </button>
            </footer>
          </form>
        </GlassPanel>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: AppUserStatus }) {
  const cls =
    status === 'active'
      ? 'glass-pill glass-pill--green'
      : status === 'pending'
        ? 'glass-pill glass-pill--orange'
        : 'glass-pill';
  return <span className={cls}>{status}</span>;
}

// --- Audit tab: agent activity & approvals ledger --------------------------

type AuditFilter = 'all' | 'pending' | 'approved' | 'dismissed';

/**
 * Audit log — the honest trail of every action an agent *proposed* and what
 * happened to it (still pending, approved + run, or dismissed). Backed by the
 * `approvals` collection (the same records the Dashboard inbox and the
 * autopilot governance gate write to), so it reflects real activity rather
 * than a mock. Newest-first.
 */
function AuditTab({ canMutate }: { canMutate: boolean }) {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [filter, setFilter] = useState<AuditFilter>('all');

  useEffect(() => {
    const unsub = subscribeApprovals((rows) => {
      setItems([...rows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
    });
    return unsub;
  }, []);

  const counts = useMemo(() => {
    const acc = { all: items.length, pending: 0, approved: 0, dismissed: 0 };
    for (const i of items) acc[i.status]++;
    return acc;
  }, [items]);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((i) => i.status === filter)),
    [items, filter],
  );

  const FILTERS: { id: AuditFilter; label: string; accent: string }[] = [
    { id: 'all', label: 'All', accent: C.blueElectric },
    { id: 'pending', label: 'Pending', accent: C.orange },
    { id: 'approved', label: 'Approved', accent: C.green },
    { id: 'dismissed', label: 'Dismissed', accent: C.textMuted },
  ];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div className="kanban-toolbar">
        <div className="kanban-phasefilter" role="tablist">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={active}
                className="kanban-phasefilter__btn"
                data-active={active}
                style={
                  {
                    ['--phase-accent' as any]: f.accent,
                    ['--phase-accent-soft' as any]: 'rgba(255,255,255,0.18)',
                  } as React.CSSProperties
                }
                onClick={() => setFilter(f.id)}
              >
                <span className="kanban-phasefilter__dot" style={{ background: f.accent }} />
                <span>{f.label}</span>
                <span className="kanban-phasefilter__count">{counts[f.id]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <GlassPanel variant="subtle" animate={false} style={{ padding: 0, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div className="crm-empty">
            <History size={20} strokeWidth={1.75} />
            <span>
              {items.length === 0
                ? 'No agent activity yet. Proposals from schedulers and the ticket autopilot land here.'
                : 'Nothing matches this filter.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'grid' }}>
            {filtered.map((item) => (
              <AuditRow key={item.id} item={item} canMutate={canMutate} />
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}

function AuditRow({ item, canMutate }: { item: ApprovalItem; canMutate: boolean }) {
  let verb = 'Action';
  let detail = '';
  try {
    const s = summarizeAction(item.action);
    verb = s.verb;
    detail = s.detail;
  } catch {
    /* unknown action shape — show raw kind */
    verb = String(item.action?.kind ?? 'Action');
  }

  const statusMeta =
    item.status === 'pending'
      ? { cls: 'glass-pill glass-pill--orange', label: 'Pending' }
      : item.status === 'approved'
        ? { cls: 'glass-pill glass-pill--green', label: 'Approved' }
        : { cls: 'glass-pill', label: 'Dismissed' };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          flex: '0 0 auto',
          borderRadius: 9,
          display: 'inline-grid',
          placeItems: 'center',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: C.magentaSoft,
        }}
      >
        <Bot size={15} strokeWidth={1.75} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="t-card-title" style={{ fontSize: 13 }}>{verb}</span>
          <span
            className="glass-pill"
            style={{ fontSize: 9, padding: '1px 6px', textTransform: 'capitalize' }}
          >
            {item.source}
          </span>
        </div>
        {detail && (
          <div
            className="t-meta"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {detail}
          </div>
        )}
        {item.note && (
          <div className="t-meta" style={{ color: C.textMuted, marginTop: 2 }}>
            {item.note}
          </div>
        )}
      </div>
      <span className="t-meta" style={{ flex: '0 0 auto', color: C.textMuted }}>
        {fmtRelative(item.resolvedAt ?? item.createdAt)}
      </span>
      <span className={statusMeta.cls} style={{ flex: '0 0 auto', fontSize: 11 }}>
        {statusMeta.label}
      </span>
      {canMutate && item.status === 'pending' && (
        <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            className="glass-btn"
            onClick={() => resolveApproval(item.id, 'dismissed')}
            title="Dismiss"
            style={{ padding: '4px 6px' }}
          >
            <X size={12} />
          </button>
          <button
            type="button"
            className="glass-btn glass-btn--primary"
            onClick={() => resolveApproval(item.id, 'approved')}
            title="Approve and run"
            style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Check size={12} /> Approve
          </button>
        </span>
      )}
    </div>
  );
}

// --- Settings tab: integration + runtime status ----------------------------

/**
 * Settings — an honest, read-only status board of what's *live* vs *simulated*.
 *
 * Every agentic capability is a provider-agnostic seam: when its `VITE_*_API_URL`
 * is set the app talks to the real hosted worker, otherwise it runs a believable
 * local simulation. This surface shows exactly which mode each seam is in and the
 * env var to set to go live — no secrets are entered here (that stays in the
 * deploy environment), so there's nothing to leak from the client.
 */
function SettingsTab({ onOpenSetup }: { onOpenSetup?: () => void }) {
  const seams: {
    name: string;
    Icon: LucideIcon;
    configured: boolean;
    envVar: string;
    blurb: string;
  }[] = [
    {
      name: 'LLM agent worker',
      Icon: Sparkles,
      configured: argoWorker.isConfigured(),
      envVar: 'VITE_AGENT_API_URL',
      blurb: 'Routes chat + ticket work through the hosted, provider-agnostic LLM pipeline.',
    },
    {
      name: 'Prospect enrichment',
      Icon: Search,
      configured: enrich.isConfigured(),
      envVar: 'VITE_ENRICH_API_URL',
      blurb: 'Evaluates LinkedIn + company signals on a prospect.',
    },
    {
      name: 'Google Workspace / Meet',
      Icon: Mail,
      configured: meetNotes.isConfigured(),
      envVar: 'VITE_GSUITE_API_URL',
      blurb: 'Pulls meeting notes and email into the activity timeline.',
    },
    {
      name: 'Slack notifications',
      Icon: Bot,
      configured: slackNotify.isConfigured(),
      envVar: 'VITE_SLACK_API_URL',
      blurb: 'Posts agent + pipeline events to a Slack channel.',
    },
  ];

  const useFirestore = import.meta.env.VITE_USE_FIRESTORE === 'true';
  const useFirebaseAuth = import.meta.env.VITE_USE_FIREBASE_AUTH === 'true';

  // Which LLM the agent worker uses. Persisted per-instance; sent on every
  // worker call. The choice only affects live calls when the worker is
  // configured — otherwise the app runs the deterministic simulation.
  const [model, setModel] = useState(getSelectedModel());
  const workerLive = argoWorker.isConfigured();
  const activeModel = findModel(model);
  const providerGroups = modelsByProvider();

  const runtime: { name: string; Icon: LucideIcon; live: boolean; on: string; off: string }[] = [
    {
      name: 'Persistence',
      Icon: Database,
      live: useFirestore,
      on: 'Firestore (cloud, shared)',
      off: 'localStorage (this browser only)',
    },
    {
      name: 'Authentication',
      Icon: KeyRound,
      live: useFirebaseAuth,
      on: 'Firebase Auth (Google SSO)',
      off: 'Local mock auth (dev)',
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {onOpenSetup && (
        <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Compass size={16} style={{ color: C.magentaSoft, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="t-card-title" style={{ fontSize: 13 }}>Guided setup</div>
              <div className="t-meta" style={{ color: C.textMuted }}>
                Walk through the company brain (voice, positioning, offers, ICP), set org
                KPIs & targets, and record governance ground rules — step by step.
              </div>
            </div>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={onOpenSetup}
              style={{ padding: '7px 14px', fontSize: 12, flexShrink: 0 }}
            >
              Open guided setup →
            </button>
          </div>
        </GlassPanel>
      )}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Database size={15} />
          <span className="t-section-title" style={{ margin: 0 }}>Runtime</span>
        </div>
        <p className="t-meta" style={{ color: C.textMuted, margin: '0 0 12px' }}>
          Where data lives and how people sign in. Flipped per environment at deploy time.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          {runtime.map((r) => {
            const Icon = r.Icon;
            return (
              <div
                key={r.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.18)',
                }}
              >
                <Icon size={16} style={{ color: C.textMuted, flex: '0 0 auto' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="t-card-title" style={{ fontSize: 13 }}>{r.name}</div>
                  <div className="t-meta" style={{ color: C.textMuted }}>
                    {r.live ? r.on : r.off}
                  </div>
                </div>
                <span
                  className={r.live ? 'glass-pill glass-pill--green' : 'glass-pill'}
                  style={{ fontSize: 11, flex: '0 0 auto' }}
                >
                  {r.live ? 'Cloud' : 'Local'}
                </span>
              </div>
            );
          })}
        </div>
      </GlassPanel>

      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <BrainCircuit size={15} />
          <span className="t-section-title" style={{ margin: 0 }}>Model</span>
        </div>
        <p className="t-meta" style={{ color: C.textMuted, margin: '0 0 12px' }}>
          Which large language model the agent worker uses for chat, drafts, and
          agent runs. Provider-agnostic — the worker routes your choice to
          Anthropic, OpenAI, or Google Gemini. The matching provider API key
          must be configured server-side on this instance.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(0,0,0,0.18)',
            flexWrap: 'wrap',
          }}
        >
          <BrainCircuit size={16} style={{ color: C.textMuted, flex: '0 0 auto' }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <label
              htmlFor="llm-model-select"
              className="t-card-title"
              style={{ fontSize: 13, display: 'block', marginBottom: 6 }}
            >
              Active model
            </label>
            <select
              id="llm-model-select"
              value={model}
              onChange={(e) => {
                setSelectedModel(e.target.value);
                setModel(e.target.value);
              }}
              style={{
                width: '100%',
                maxWidth: 380,
                padding: '7px 10px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.35)',
                color: C.text,
                fontSize: 13,
              }}
            >
              {providerGroups.map((g) => (
                <optgroup key={g.provider} label={PROVIDER_LABEL[g.provider]}>
                  {g.models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                      {m.recommended ? ' — recommended' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {activeModel && (
              <div className="t-meta" style={{ color: C.textMuted, marginTop: 6 }}>
                {PROVIDER_LABEL[activeModel.provider]} · {activeModel.blurb}{' '}
                <code
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                >
                  {activeModel.id}
                </code>
              </div>
            )}
          </div>
          <span
            className={workerLive ? 'glass-pill glass-pill--green' : 'glass-pill'}
            style={{
              fontSize: 11,
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
            title={
              workerLive
                ? 'The agent worker is configured — this model is used for live calls.'
                : 'No agent worker configured (VITE_AGENT_API_URL) — the app runs the deterministic simulation, so this choice has no effect yet.'
            }
          >
            {workerLive ? <Sparkles size={11} /> : <CircleDashed size={11} />}
            {workerLive ? 'Applied to live calls' : 'Applies when worker connected'}
          </span>
        </div>
      </GlassPanel>

      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Sparkles size={15} />
          <span className="t-section-title" style={{ margin: 0 }}>Integrations</span>
        </div>
        <p className="t-meta" style={{ color: C.textMuted, margin: '0 0 12px' }}>
          Each capability runs a local simulation until its worker URL is set in the
          deploy environment — then it goes live with no code change. Secrets live
          server-side; nothing sensitive is entered here.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          {seams.map((s) => {
            const Icon = s.Icon;
            return (
              <div
                key={s.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.18)',
                }}
              >
                <Icon size={16} style={{ color: C.textMuted, flex: '0 0 auto' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="t-card-title" style={{ fontSize: 13 }}>{s.name}</div>
                  <div className="t-meta" style={{ color: C.textMuted }}>{s.blurb}</div>
                  <code
                    style={{
                      fontSize: 10,
                      color: C.textMuted,
                      fontFamily: 'var(--font-mono, monospace)',
                    }}
                  >
                    {s.envVar}
                  </code>
                </div>
                <span
                  className={s.configured ? 'glass-pill glass-pill--green' : 'glass-pill'}
                  style={{
                    fontSize: 11,
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {s.configured ? <Sparkles size={11} /> : <CircleDashed size={11} />}
                  {s.configured ? 'Connected' : 'Simulation'}
                </span>
              </div>
            );
          })}
        </div>
      </GlassPanel>

      <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ShieldCheck size={15} />
          <span className="t-section-title" style={{ margin: 0 }}>Governance</span>
        </div>
        <p className="t-meta" style={{ color: C.textMuted, margin: 0 }}>
          Agents never write silently. Schedulers and the ticket autopilot queue
          proposals to the approvals inbox; a human approves before anything
          executes, and every decision is recorded in the Audit log.
        </p>
      </GlassPanel>
    </div>
  );
}

function fmtRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// Suppress unused-import warnings for icons consumed via `tabs`.
void Shield;
void UserCircle2;
